// ============================================================================
// AUDIT LOG VIEWER PAGE
// Admin page for viewing and analyzing audit logs - Tailwind styling
// ============================================================================

import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  Search,
  Download,
  Shield,
  Calendar,
  AlertCircle,
  CheckCircle,
  XCircle,
  Clock,
  ChevronDown,
  ChevronUp,
  ChevronLeft,
  ChevronRight,
  FileText,
  RefreshCw
} from 'lucide-react';
import {
  AuditEvent,
  AuditEventStatistics,
  listAuditEvents,
  getEventTypes,
  getAuditStatistics,
  verifyHashChain,
  rebuildHashChain,
  exportAuditEventsToCSV,
  exportAuditEventsToJSON,
  downloadCSV,
  downloadJSON,
  EventTypeCategories
} from '../services/auditLogService';

const AuditLogViewer: React.FC = () => {
  const { can } = useAuth();

  const [events, setEvents] = useState<AuditEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [eventTypeFilter, setEventTypeFilter] = useState('');
  const [severityFilter, setSeverityFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [showAllOrgs, setShowAllOrgs] = useState(false);
  const [eventTypes, setEventTypes] = useState<EventTypeCategories | null>(null);
  const [expandedEvent, setExpandedEvent] = useState<string | null>(null);
  const [verifyingChain, setVerifyingChain] = useState(false);
  const [verificationResult, setVerificationResult] = useState<any>(null);
  const [rebuildingChain, setRebuildingChain] = useState(false);
  const [rebuildResult, setRebuildResult] = useState<any>(null);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [nextToken, setNextToken] = useState<string | undefined>(undefined);
  const [hasMorePages, setHasMorePages] = useState(false);
  const [statistics, setStatistics] = useState<AuditEventStatistics | null>(null);
  const [showStatistics, setShowStatistics] = useState(false);
  const [loadingStats, setLoadingStats] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [refreshInterval, setRefreshInterval] = useState(30);
  const [selectedEvents, setSelectedEvents] = useState<Set<string>>(new Set());
  const [totalCount, setTotalCount] = useState<number>(0);
  const itemsPerPage = 50;

  useEffect(() => {
    fetchEventTypes();
  }, []);

  const fetchStatistics = useCallback(async () => {
    try {
      setLoadingStats(true);
      const stats = await getAuditStatistics(showAllOrgs);
      setStatistics(stats);
    } catch (err: any) {
      console.error('Failed to fetch statistics:', err);
    } finally {
      setLoadingStats(false);
    }
  }, [showAllOrgs]);

  const fetchAuditEvents = useCallback(async (pageToken?: string) => {
    try {
      setLoading(true);
      setError(null);

      const result = await listAuditEvents({
        limit: itemsPerPage,
        allOrganizations: showAllOrgs,
        startDate: startDate || undefined,
        endDate: endDate || undefined,
        eventType: eventTypeFilter || undefined,
        severity: severityFilter || undefined,
        status: statusFilter || undefined,
        nextToken: pageToken
      });

      setEvents(result.events);
      setTotalCount(result.total ?? 0);
      setNextToken(result.nextToken);
      setHasMorePages(!!result.nextToken);
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to fetch audit events');
    } finally {
      setLoading(false);
    }
  }, [showAllOrgs, startDate, endDate, eventTypeFilter, severityFilter, statusFilter]);

  useEffect(() => {
    setCurrentPage(1);
    setNextToken(undefined);
    fetchAuditEvents();
  }, [fetchAuditEvents]);

  useEffect(() => {
    if (!autoRefresh) return;
    const intervalId = setInterval(() => {
      fetchAuditEvents();
      if (showStatistics) fetchStatistics();
    }, refreshInterval * 1000);
    return () => clearInterval(intervalId);
  }, [autoRefresh, refreshInterval, showStatistics, fetchAuditEvents, fetchStatistics]);

  const fetchEventTypes = async () => {
    try {
      const types = await getEventTypes();
      setEventTypes(types);
    } catch (err: any) {
      console.error('Failed to fetch event types:', err);
    }
  };

  const toggleStatistics = () => {
    setShowStatistics(!showStatistics);
    if (!showStatistics && !statistics) fetchStatistics();
  };

  const handleNextPage = () => {
    if (nextToken) {
      setCurrentPage(prev => prev + 1);
      fetchAuditEvents(nextToken);
    }
  };

  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(1);
      setNextToken(undefined);
      fetchAuditEvents();
    }
  };

  const handleClearDateRange = () => {
    setStartDate('');
    setEndDate('');
  };

  const handleVerifyHashChain = async () => {
    try {
      setVerifyingChain(true);
      const result = await verifyHashChain(100);
      setVerificationResult(result);
      setTimeout(() => setVerificationResult(null), 5000);
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to verify hash chain');
    } finally {
      setVerifyingChain(false);
    }
  };

  const handleRebuildHashChain = async () => {
    const confirmed = window.confirm(
      'WARNING: This will recalculate all event hashes for your organization.\n\n' +
      'This operation should only be used when the hash chain is broken.\n\n' +
      'Do you want to continue?'
    );
    if (!confirmed) return;
    try {
      setRebuildingChain(true);
      setRebuildResult(null);
      const result = await rebuildHashChain();
      setRebuildResult(result);
      await fetchAuditEvents();
      setTimeout(() => setRebuildResult(null), 8000);
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to rebuild hash chain');
    } finally {
      setRebuildingChain(false);
    }
  };

  const handleExportCSV = () => {
    const csv = exportAuditEventsToCSV(filteredEvents);
    downloadCSV(csv, `audit-logs-${new Date().toISOString().split('T')[0]}.csv`);
  };

  const handleExportJSON = () => {
    const json = exportAuditEventsToJSON(filteredEvents);
    downloadJSON(json, `audit-logs-${new Date().toISOString().split('T')[0]}.json`);
  };

  const handleBulkExportCSV = () => {
    const selected = filteredEvents.filter(e => selectedEvents.has(e.eventId));
    const csv = exportAuditEventsToCSV(selected);
    downloadCSV(csv, `audit-logs-selected-${new Date().toISOString().split('T')[0]}.csv`);
  };

  const handleBulkExportJSON = () => {
    const selected = filteredEvents.filter(e => selectedEvents.has(e.eventId));
    const json = exportAuditEventsToJSON(selected);
    downloadJSON(json, `audit-logs-selected-${new Date().toISOString().split('T')[0]}.json`);
  };

  const handleToggleEvent = (eventId: string) => {
    const newSelected = new Set(selectedEvents);
    if (newSelected.has(eventId)) newSelected.delete(eventId);
    else newSelected.add(eventId);
    setSelectedEvents(newSelected);
  };

  const handleSelectAll = () => {
    if (selectedEvents.size === filteredEvents.length) setSelectedEvents(new Set());
    else setSelectedEvents(new Set(filteredEvents.map(e => e.eventId)));
  };

  const handleClearSelection = () => {
    setSelectedEvents(new Set());
  };

  const filteredEvents = events.filter(event => {
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      const matchesSearch =
        event.eventId.toLowerCase().includes(searchLower) ||
        event.actorUserId.toLowerCase().includes(searchLower) ||
        event.action.toLowerCase().includes(searchLower) ||
        event.eventType.toLowerCase().includes(searchLower) ||
        (event.resourceId && event.resourceId.toLowerCase().includes(searchLower));
      if (!matchesSearch) return false;
    }
    if (eventTypeFilter && event.eventType !== eventTypeFilter) return false;
    if (severityFilter && event.severity !== severityFilter) return false;
    if (statusFilter && event.status !== statusFilter) return false;
    return true;
  });

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'success': return { Icon: CheckCircle, classes: 'text-emerald-600' };
      case 'failure': return { Icon: XCircle, classes: 'text-red-600' };
      case 'pending': return { Icon: Clock, classes: 'text-amber-500' };
      default: return { Icon: AlertCircle, classes: 'text-slate-400' };
    }
  };

  const getSeverityClasses = (severity: string) => {
    switch (severity) {
      case 'critical': return 'bg-red-100 text-red-700';
      case 'error': return 'bg-red-50 text-red-600';
      case 'warning': return 'bg-amber-100 text-amber-700';
      case 'info': return 'bg-blue-50 text-blue-700';
      default: return 'bg-slate-100 text-slate-600';
    }
  };

  const getSeverityDotClass = (severity: string) => {
    switch (severity) {
      case 'critical': return 'bg-red-600';
      case 'error': return 'bg-red-500';
      case 'warning': return 'bg-amber-500';
      case 'info': return 'bg-blue-600';
      default: return 'bg-slate-400';
    }
  };

  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    return date.toLocaleString('en-US', {
      year: 'numeric', month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit', second: '2-digit'
    });
  };

  const renderStatisticsContent = () => {
    if (loadingStats) {
      return <div className="text-center py-10 text-slate-500">Loading statistics...</div>;
    }
    if (!statistics) {
      return <div className="text-center py-10 text-slate-500">Failed to load statistics</div>;
    }
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
        {/* Total Events */}
        <div className="p-5 bg-slate-50 rounded-xl border border-[#e2e8f0]">
          <div className="text-[11px] font-bold text-slate-400 uppercase tracking-widest mb-2">Total Events</div>
          <div className="text-3xl font-bold text-navy">{statistics.totalEvents.toLocaleString()}</div>
        </div>

        {/* Events by Severity */}
        <div className="p-5 bg-slate-50 rounded-xl border border-[#e2e8f0]">
          <div className="text-[11px] font-bold text-slate-400 uppercase tracking-widest mb-3">By Severity</div>
          <div className="flex flex-col gap-2">
            {Object.entries(statistics.eventsBySeverity)
              .sort(([, a], [, b]) => b - a)
              .map(([severity, count]) => (
                <div key={severity} className="flex justify-between items-center text-sm">
                  <span className="flex items-center gap-2 capitalize">
                    <div className={`w-2 h-2 rounded-full ${getSeverityDotClass(severity)}`} />
                    {severity}
                  </span>
                  <span className="font-semibold text-slate-600">{count.toLocaleString()}</span>
                </div>
              ))}
          </div>
        </div>

        {/* Events by Status */}
        <div className="p-5 bg-slate-50 rounded-xl border border-[#e2e8f0]">
          <div className="text-[11px] font-bold text-slate-400 uppercase tracking-widest mb-3">By Status</div>
          <div className="flex flex-col gap-2">
            {Object.entries(statistics.eventsByStatus)
              .sort(([, a], [, b]) => b - a)
              .map(([status, count]) => {
                const { Icon, classes } = getStatusIcon(status);
                return (
                  <div key={status} className="flex justify-between items-center text-sm">
                    <span className="flex items-center gap-2 capitalize">
                      <Icon size={16} className={classes} />
                      {status}
                    </span>
                    <span className="font-semibold text-slate-600">{count.toLocaleString()}</span>
                  </div>
                );
              })}
          </div>
        </div>

        {/* Top Event Types */}
        <div className="p-5 bg-slate-50 rounded-xl border border-[#e2e8f0]">
          <div className="text-[11px] font-bold text-slate-400 uppercase tracking-widest mb-3">Top Event Types</div>
          <div className="flex flex-col gap-2">
            {Object.entries(statistics.eventsByType)
              .sort(([, a], [, b]) => b - a)
              .slice(0, 5)
              .map(([type, count]) => (
                <div key={type} className="flex justify-between items-center text-[13px]">
                  <span className="truncate flex-1 mr-3 text-slate-600">{type}</span>
                  <span className="font-semibold text-slate-600">{count.toLocaleString()}</span>
                </div>
              ))}
          </div>
        </div>

        {/* Time Range */}
        {statistics.timeRange && (
          <div className="p-5 bg-slate-50 rounded-xl border border-[#e2e8f0]">
            <div className="text-[11px] font-bold text-slate-400 uppercase tracking-widest mb-3">Time Range</div>
            <div className="flex flex-col gap-2 text-[13px]">
              <div>
                <div className="text-xs text-slate-400">Earliest</div>
                <div className="font-medium text-slate-600">{formatTimestamp(statistics.timeRange.earliest)}</div>
              </div>
              <div>
                <div className="text-xs text-slate-400">Latest</div>
                <div className="font-medium text-slate-600">{formatTimestamp(statistics.timeRange.latest)}</div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="flex-1 overflow-y-auto p-8 font-display">
    <div className="w-full max-w-[1400px] mx-auto flex flex-col gap-6">
      <header className="flex items-center justify-between border-b border-slate-200 pb-6">
        <div className="flex items-center gap-4">
          <div className="flex items-center justify-center w-10 h-10 rounded-full bg-primary/10 text-primary">
            <Shield size={22} />
          </div>
          <div>
            <h1 className="text-2xl font-bold tracking-tight text-navy">Audit Logs</h1>
            <p className="text-slate-500 text-sm font-medium">View and analyze system audit trail with tamper detection</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handleExportCSV}
            className="flex items-center justify-center gap-2 rounded-xl h-10 px-4 border border-slate-200 bg-white text-slate-600 text-sm font-bold hover:bg-slate-50 shadow-sm transition-colors"
          >
            <Download size={16} /> CSV
          </button>
          <button
            onClick={handleExportJSON}
            className="flex items-center justify-center gap-2 rounded-xl h-10 px-4 border border-slate-200 bg-white text-slate-600 text-sm font-bold hover:bg-slate-50 shadow-sm transition-colors"
          >
            <FileText size={16} /> JSON
          </button>
        </div>
      </header>

      {/* Statistics Dashboard */}
      <div className="bg-white border border-[#e2e8f0] rounded-2xl overflow-hidden">
        <button
          onClick={toggleStatistics}
          className="w-full px-5 py-4 flex items-center justify-between hover:bg-slate-50 transition-colors"
        >
          <div className="flex items-center gap-3 text-base font-semibold text-navy">
            <Shield size={20} className="text-primary" />
            Statistics Dashboard
          </div>
          {showStatistics ? <ChevronUp size={20} className="text-slate-400" /> : <ChevronDown size={20} className="text-slate-400" />}
        </button>

        {showStatistics && (
          <div className="px-5 pb-5 pt-4 border-t border-[#e2e8f0]">
            {renderStatisticsContent()}
          </div>
        )}
      </div>

      {/* Verification Result Banner */}
      {verificationResult && (
        <div className={`p-4 mb-6 rounded-xl flex items-center gap-3 ${verificationResult.isValid ? 'bg-emerald-50 border border-emerald-300' : 'bg-red-50 border border-red-300'}`}>
          {verificationResult.isValid ? (
            <CheckCircle size={20} className="text-emerald-600" />
          ) : (
            <XCircle size={20} className="text-red-600" />
          )}
          <span className="flex-1 text-sm font-medium">{verificationResult.message}</span>
        </div>
      )}

      {/* Rebuild Result Banner */}
      {rebuildResult && (
        <div className={`p-4 mb-6 rounded-xl ${rebuildResult.success ? 'bg-emerald-50 border border-emerald-300' : 'bg-red-50 border border-red-300'}`}>
          <div className="flex items-center gap-3 mb-2">
            {rebuildResult.success ? (
              <CheckCircle size={20} className="text-emerald-600" />
            ) : (
              <XCircle size={20} className="text-red-600" />
            )}
            <span className="flex-1 text-sm font-semibold">{rebuildResult.message}</span>
          </div>
          <div className="text-[13px] text-slate-600 ml-8">
            <div>Events Processed: {rebuildResult.eventsProcessed}</div>
            <div>Events Updated: {rebuildResult.eventsUpdated}</div>
            {rebuildResult.errors && rebuildResult.errors.length > 0 && (
              <div className="text-red-600 mt-1">Errors: {rebuildResult.errors.length}</div>
            )}
          </div>
        </div>
      )}

      {/* Filters & Controls */}
      <div className="bg-white border border-[#e2e8f0] rounded-2xl p-5">
        {/* Search & Filters Row */}
        <div className="grid grid-cols-[1fr_auto_auto_auto] gap-3 mb-4 items-center">
          <div className="relative">
            <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              placeholder="Search events, user IDs, actions..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 border border-[#e2e8f0] rounded-xl text-sm text-navy placeholder:text-slate-400 focus:ring-2 focus:ring-primary focus:border-transparent outline-none"
            />
          </div>

          <select
            value={eventTypeFilter}
            onChange={(e) => setEventTypeFilter(e.target.value)}
            className="px-3 py-2.5 border border-[#e2e8f0] rounded-xl text-sm cursor-pointer focus:outline-none focus:border-primary min-w-[150px]"
          >
            <option value="">All Types</option>
            {eventTypes?.allTypes.map(type => (
              <option key={type} value={type}>{type}</option>
            ))}
          </select>

          <select
            value={severityFilter}
            onChange={(e) => setSeverityFilter(e.target.value)}
            className="px-3 py-2.5 border border-[#e2e8f0] rounded-xl text-sm cursor-pointer focus:outline-none focus:border-primary min-w-[130px]"
          >
            <option value="">All Severities</option>
            <option value="info">Info</option>
            <option value="warning">Warning</option>
            <option value="error">Error</option>
            <option value="critical">Critical</option>
          </select>

          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-3 py-2.5 border border-[#e2e8f0] rounded-xl text-sm cursor-pointer focus:outline-none focus:border-primary min-w-[130px]"
          >
            <option value="">All Statuses</option>
            <option value="success">Success</option>
            <option value="failure">Failure</option>
            <option value="pending">Pending</option>
          </select>
        </div>

        {/* Date Range Row */}
        <div className="flex gap-3 pt-4 border-t border-[#e2e8f0] items-center flex-wrap">
          <div className="flex items-center gap-2">
            <Calendar size={16} className="text-slate-400" />
            <span className="text-[13px] font-semibold text-slate-600">Date Range:</span>
          </div>
          <input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            className="px-3 py-2 border border-[#e2e8f0] rounded-lg text-[13px] focus:outline-none focus:border-primary cursor-pointer"
          />
          <span className="text-slate-400 text-sm">to</span>
          <input
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            className="px-3 py-2 border border-[#e2e8f0] rounded-lg text-[13px] focus:outline-none focus:border-primary cursor-pointer"
          />
          {(startDate || endDate) && (
            <button
              onClick={handleClearDateRange}
              className="flex items-center gap-1 px-2.5 py-1.5 bg-slate-50 text-slate-600 border border-[#e2e8f0] rounded-lg text-xs font-medium hover:bg-slate-100 transition-colors"
            >
              <XCircle size={12} />
              Clear
            </button>
          )}
        </div>
      </div>

      {/* Action Buttons Panel */}
      <div className="bg-white border border-[#e2e8f0] rounded-2xl p-4 flex justify-between items-center flex-wrap gap-3">
        {/* Left side */}
        <div className="flex gap-3 flex-wrap items-center">
          {can('Admin::ViewAudit') && (
            <label className={`flex items-center gap-2 px-3 py-2 rounded-lg text-[13px] font-medium cursor-pointer border transition-colors ${showAllOrgs ? 'bg-blue-50 border-blue-400 text-blue-700' : 'bg-slate-50 border-[#e2e8f0] text-slate-600'}`}>
              <input
                type="checkbox"
                checked={showAllOrgs}
                onChange={(e) => setShowAllOrgs(e.target.checked)}
                className="cursor-pointer"
              />
              All Orgs
            </label>
          )}

          <label className={`flex items-center gap-2 px-3 py-2 rounded-lg text-[13px] font-medium cursor-pointer border transition-colors ${autoRefresh ? 'bg-emerald-50 border-emerald-400 text-emerald-700' : 'bg-slate-50 border-[#e2e8f0] text-slate-600'}`}>
            <input
              type="checkbox"
              checked={autoRefresh}
              onChange={(e) => setAutoRefresh(e.target.checked)}
              className="cursor-pointer"
            />
            <RefreshCw size={14} />
            {autoRefresh ? `${refreshInterval}s` : 'Auto-Refresh'}
          </label>

          {autoRefresh && (
            <select
              value={refreshInterval}
              onChange={(e) => setRefreshInterval(parseInt(e.target.value))}
              className="px-2.5 py-2 border border-[#e2e8f0] rounded-lg text-[13px] focus:outline-none cursor-pointer bg-white"
            >
              <option value="10">10s</option>
              <option value="30">30s</option>
              <option value="60">60s</option>
              <option value="120">2min</option>
              <option value="300">5min</option>
            </select>
          )}

          <button
            onClick={() => {
              fetchAuditEvents();
              if (showStatistics) fetchStatistics();
            }}
            disabled={loading}
            className="flex items-center gap-1.5 px-3 py-2 bg-white text-slate-600 border border-[#e2e8f0] rounded-lg text-[13px] font-medium hover:bg-slate-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <RefreshCw size={14} />
            Refresh
          </button>
        </div>

        {/* Right side */}
        <div className="flex gap-2 flex-wrap">
          <button
            onClick={handleVerifyHashChain}
            disabled={verifyingChain}
            className="flex items-center gap-1.5 px-3 py-2 bg-blue-600 text-white rounded-lg text-[13px] font-medium hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Shield size={14} />
            {verifyingChain ? 'Verifying...' : 'Verify'}
          </button>

          <button
            onClick={handleRebuildHashChain}
            disabled={rebuildingChain}
            className="flex items-center gap-1.5 px-3 py-2 bg-amber-500 text-white rounded-lg text-[13px] font-medium hover:bg-amber-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Shield size={14} />
            {rebuildingChain ? 'Rebuilding...' : 'Rebuild'}
          </button>

          <button
            onClick={handleExportCSV}
            className="flex items-center gap-1.5 px-3 py-2 bg-emerald-600 text-white rounded-lg text-[13px] font-medium hover:bg-emerald-700 transition-colors"
          >
            <Download size={14} />
            CSV
          </button>

          <button
            onClick={handleExportJSON}
            className="flex items-center gap-1.5 px-3 py-2 bg-slate-600 text-white rounded-lg text-[13px] font-medium hover:bg-slate-700 transition-colors"
          >
            <FileText size={14} />
            JSON
          </button>
        </div>
      </div>

      {/* Results Count */}
      <div className="mb-4 text-sm text-slate-500">
        Showing {filteredEvents.length} of {totalCount} events
      </div>

      {/* Batch Operations Bar */}
      {selectedEvents.size > 0 && (
        <div className="p-4 mb-4 bg-blue-50 border border-blue-400 rounded-xl flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <span className="text-sm font-semibold text-blue-700">
              {selectedEvents.size} event{selectedEvents.size !== 1 ? 's' : ''} selected
            </span>
            <button
              onClick={handleClearSelection}
              className="px-3 py-1.5 bg-white text-slate-600 border border-blue-400 rounded-lg text-[13px] font-medium hover:bg-slate-50 transition-colors"
            >
              Clear Selection
            </button>
          </div>
          <div className="flex gap-2 flex-wrap">
            <button
              onClick={handleBulkExportCSV}
              className="flex items-center gap-1.5 px-3.5 py-2 bg-emerald-600 text-white rounded-lg text-[13px] font-medium hover:bg-emerald-700 transition-colors"
            >
              <Download size={14} />
              Export Selected (CSV)
            </button>
            <button
              onClick={handleBulkExportJSON}
              className="flex items-center gap-1.5 px-3.5 py-2 bg-slate-600 text-white rounded-lg text-[13px] font-medium hover:bg-slate-700 transition-colors"
            >
              <FileText size={14} />
              Export Selected (JSON)
            </button>
          </div>
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div className="flex justify-center items-center h-40">
          <svg className="w-6 h-6 animate-spin text-slate-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
          </svg>
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="p-4 rounded-xl bg-red-50 border border-red-200 flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
          <p className="text-sm text-red-700 flex-1">{error}</p>
          <button onClick={() => setError(null)} className="text-red-400 hover:text-red-600">
            <XCircle className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Events Table */}
      {!loading && !error && (
        <div className="bg-white border border-[#e2e8f0] rounded-2xl overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[#e2e8f0] bg-slate-50/50">
                <th className="px-4 py-3 text-center w-[50px]">
                  <input
                    type="checkbox"
                    checked={selectedEvents.size === filteredEvents.length && filteredEvents.length > 0}
                    onChange={handleSelectAll}
                    className="cursor-pointer accent-primary"
                    title="Select all events"
                  />
                </th>
                <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-slate-400">Timestamp</th>
                <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-slate-400">Event Type</th>
                <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-slate-400">Action</th>
                <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-slate-400">User ID</th>
                <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-slate-400">Status</th>
                <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-slate-400">Severity</th>
                <th className="px-4 py-3 text-center text-xs font-bold uppercase tracking-wider text-slate-400">Details</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#e2e8f0]">
              {filteredEvents.map((event) => {
                const { Icon: StatusIcon, classes: statusClasses } = getStatusIcon(event.status);
                const isExpanded = expandedEvent === event.eventId;

                return (
                  <React.Fragment key={event.eventId}>
                    <tr className={`transition-colors ${isExpanded ? 'bg-slate-50' : 'hover:bg-slate-50'}`}>
                      <td className="px-4 py-3 text-center">
                        <input
                          type="checkbox"
                          checked={selectedEvents.has(event.eventId)}
                          onChange={() => handleToggleEvent(event.eventId)}
                          onClick={(e) => e.stopPropagation()}
                          className="cursor-pointer"
                        />
                      </td>
                      <td className="px-4 py-3 text-[13px] text-slate-600">{formatTimestamp(event.timestamp)}</td>
                      <td className="px-4 py-3">
                        <span className="px-2 py-1 bg-blue-50 text-blue-700 rounded text-xs font-medium font-mono">
                          {event.eventType}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-[13px] text-navy">{event.action}</td>
                      <td className="px-4 py-3">
                        <span className="text-xs font-mono text-slate-600">{event.actorUserId}</span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1.5">
                          <StatusIcon size={16} className={statusClasses} />
                          <span className={`text-[13px] font-medium capitalize ${statusClasses}`}>{event.status}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 rounded text-xs font-medium capitalize ${getSeverityClasses(event.severity)}`}>
                          {event.severity}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <button
                          onClick={() => setExpandedEvent(isExpanded ? null : event.eventId)}
                          className="p-1 rounded hover:bg-slate-200 transition-colors mx-auto flex items-center justify-center"
                        >
                          {isExpanded ? <ChevronUp size={18} className="text-slate-500" /> : <ChevronDown size={18} className="text-slate-500" />}
                        </button>
                      </td>
                    </tr>

                    {/* Expanded Details */}
                    {isExpanded && (
                      <tr className="bg-slate-50/50 border-b border-[#e2e8f0]">
                        <td colSpan={8} className="p-5">
                          <div className="grid grid-cols-2 gap-4 text-[13px]">
                            <div>
                              <strong className="text-slate-600">Event ID:</strong>
                              <div className="font-mono text-slate-400 mt-1">{event.eventId}</div>
                            </div>
                            <div>
                              <strong className="text-slate-600">Organization ID:</strong>
                              <div className="font-mono text-slate-400 mt-1">{event.organizationId}</div>
                            </div>
                            {event.resourceType && (
                              <div>
                                <strong className="text-slate-600">Resource:</strong>
                                <div className="text-slate-400 mt-1">
                                  {event.resourceType} {event.resourceId && `(${event.resourceId})`}
                                </div>
                              </div>
                            )}
                            {event.actorIpAddress && (
                              <div>
                                <strong className="text-slate-600">IP Address:</strong>
                                <div className="font-mono text-slate-400 mt-1">{event.actorIpAddress}</div>
                              </div>
                            )}
                            {event.errorMessage && (
                              <div className="col-span-2">
                                <strong className="text-red-600">Error:</strong>
                                <div className="text-red-600 mt-1">{event.errorMessage}</div>
                              </div>
                            )}
                            <div className="col-span-2">
                              <strong className="text-slate-600">Event Hash:</strong>
                              <div className="font-mono text-[11px] text-slate-400 mt-1 break-all">{event.eventHash}</div>
                            </div>
                            {event.previousEventHash && (
                              <div className="col-span-2">
                                <strong className="text-slate-600">Previous Hash (Chain Link):</strong>
                                <div className="font-mono text-[11px] text-slate-400 mt-1 break-all">{event.previousEventHash}</div>
                              </div>
                            )}
                            {event.metadata && Object.keys(event.metadata).length > 0 && (
                              <div className="col-span-2">
                                <strong className="text-slate-600">Metadata:</strong>
                                <pre className="bg-white p-3 rounded-lg text-[11px] overflow-auto mt-1 text-slate-600 border border-[#e2e8f0]">
                                  {JSON.stringify(event.metadata, null, 2)}
                                </pre>
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>

          {/* Empty State */}
          {filteredEvents.length === 0 && !loading && (
            <div className="text-center py-16">
              <AlertCircle size={48} className="mx-auto text-slate-300 mb-4" />
              <div className="text-base font-medium text-slate-500">No audit events found</div>
              <div className="text-sm text-slate-400 mt-2">Try adjusting your filters or search criteria</div>
            </div>
          )}
        </div>
      )}

      {/* Pagination */}
      {!loading && !error && filteredEvents.length > 0 && (
        <div className="flex justify-between items-center mt-6 p-4 bg-white border border-[#e2e8f0] rounded-xl">
          <div className="text-sm text-slate-500">
            Page {currentPage} {hasMorePages && '· More results available'}
          </div>
          <div className="flex gap-2">
            <button
              onClick={handlePrevPage}
              disabled={currentPage === 1}
              className="flex items-center gap-1.5 px-4 py-2 border border-[#e2e8f0] rounded-lg text-sm font-medium transition-colors disabled:text-slate-300 disabled:cursor-not-allowed enabled:text-slate-600 enabled:hover:bg-slate-50"
            >
              <ChevronLeft size={16} />
              Previous
            </button>
            <button
              onClick={handleNextPage}
              disabled={!hasMorePages}
              className="flex items-center gap-1.5 px-4 py-2 border border-[#e2e8f0] rounded-lg text-sm font-medium transition-colors disabled:text-slate-300 disabled:cursor-not-allowed enabled:text-slate-600 enabled:hover:bg-slate-50"
            >
              Next
              <ChevronRight size={16} />
            </button>
          </div>
        </div>
      )}
    </div>
    </div>
  );
};

export default AuditLogViewer;
